/**
 * The one requestAnimationFrame loop. Frame time only animates; `dt` is clamped to 50 ms.
 *
 * Frame rate policy: full rate while something is happening (a touch, the camera moving, a pop),
 * 30 fps for ambient motion alone (clouds, idle bob), and nothing while the page is hidden.
 * Anything that moves calls `wake(ms)` to keep the full rate for that long.
 */
export interface Loop {
  start(): void;
  stop(): void;
  /** Keep the full frame rate for `ms` milliseconds from now. */
  wake(ms?: number): void;
  /** Whether the page is visible and the loop running. */
  running(): boolean;
}

/** `full`: the frame runs at full rate (something is happening), not as ambient motion. */
export type FrameHandler = (dt: number, now: number, full: boolean) => void;

const MAX_DT = 0.05;
const AMBIENT_INTERVAL = 1000 / 30;

export function createLoop(onFrame: FrameHandler): Loop {
  let handle = 0;
  let last = 0;
  let lastRender = 0;
  let awakeUntil = 0;
  let active = false;

  const frame = (now: number): void => {
    handle = requestAnimationFrame(frame);
    const full = now < awakeUntil;
    if (!full && now - lastRender < AMBIENT_INTERVAL - 1) return;
    const dt = last ? Math.min(MAX_DT, (now - last) / 1000) : 0;
    last = now;
    lastRender = now;
    onFrame(dt, now, full);
  };

  const run = (): void => {
    if (active || document.hidden) return;
    active = true;
    last = 0;
    handle = requestAnimationFrame(frame);
  };
  const halt = (): void => {
    if (!active) return;
    active = false;
    cancelAnimationFrame(handle);
  };
  const onVisibility = (): void => {
    if (document.hidden) halt();
    else {
      run();
      awakeUntil = performance.now() + 1000;
    }
  };

  return {
    start() {
      document.addEventListener('visibilitychange', onVisibility);
      run();
      awakeUntil = performance.now() + 1000;
    },
    stop() {
      document.removeEventListener('visibilitychange', onVisibility);
      halt();
    },
    wake(ms = 600) {
      awakeUntil = Math.max(awakeUntil, performance.now() + ms);
    },
    running: () => active,
  };
}
